/**
 * Email Flow Visualization Page
 * Allows admins to visualize the current email flow for any user
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Mail, 
  Search, 
  RefreshCw, 
  User, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Calendar,
  AlertCircle,
  Pause,
  Play,
  Edit2,
  Save,
  X,
  Eye,
  FileText
} from 'lucide-react';

import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import DOMPurify from 'dompurify';
import { db } from '../../services/firebase/config';


// Email flow configuration (mirror of backend)
const EMAIL_FLOWS: Record<string, { name: string; description: string; steps: Array<{ step: number; templateId: string; delay: { minutes?: number; hours?: number; days?: number }; condition: string | null }> }> = {
  leadMagnet: {
    name: 'Lead Magnet Download',
    description: 'Nurture leads who downloaded e-book to free registration',
    steps: [
      { step: 1, templateId: 'lead-magnet-1', delay: { hours: 1 }, condition: null },
      { step: 2, templateId: 'lead-magnet-2', delay: { days: 2 }, condition: '!registered' },
      { step: 3, templateId: 'lead-magnet-3', delay: { days: 5 }, condition: '!registered' },
    ]
  },
  freeRegistration: {
    name: 'Free Registration Onboarding',
    description: 'Onboard free users and encourage upgrade',
    steps: [
      { step: 1, templateId: 'free-welcome', delay: { minutes: 0 }, condition: null },
      { step: 2, templateId: 'free-ethics-course', delay: { days: 2 }, condition: '!upgraded' },
      { step: 3, templateId: 'free-testimonial', delay: { days: 5 }, condition: '!upgraded' },
      { step: 4, templateId: 'free-upgrade-benefits', delay: { days: 7 }, condition: '!upgraded' },
      { step: 5, templateId: 'free-AIreadiness', delay: { days: 10 }, condition: '!upgraded' },
    ]
  },
  paidMembership: {
    name: 'Paid Membership Onboarding',
    description: 'Onboard paid members and drive engagement',
    steps: [
      { step: 1, templateId: 'paid-welcome-standard', delay: { minutes: 0 }, condition: null },
      { step: 2, templateId: 'paid-maximize-membership', delay: { days: 2 }, condition: null },
      { step: 3, templateId: 'paid-first-sessions-standard', delay: { days: 5 }, condition: null },
      { step: 4, templateId: 'paid-testimonial', delay: { days: 7 }, condition: null },
      { step: 5, templateId: 'paid-feedback', delay: { days: 8 }, condition: null },
      { step: 6, templateId: 'paid-community-checkin', delay: { days: 14 }, condition: null },
    ]
  },
  cancellation: {
    name: 'Cancellation Flow',
    description: 'Handle subscription cancellations gracefully',
    steps: [
      { step: 1, templateId: 'cancellation-early', delay: { minutes: 0 }, condition: null },
      { step: 2, templateId: 'cancellation-rejoin', delay: { days: 10 }, condition: null },
    ]
  },
  incompleteRegistration: {
    name: 'Incomplete Registration Recovery',
    description: 'Re-engage users who started but didn\'t complete registration',
    steps: [
      { step: 1, templateId: 'incomplete-registration', delay: { hours: 1 }, condition: null },
    ]
  },
  twelveMonthPackage: {
    name: '12-Month Package Onboarding',
    description: 'Onboard 12-month package users with reminders and engagement',
    steps: [
      { step: 1, templateId: 'twelve-month-welcome', delay: { minutes: 0 }, condition: null },
      { step: 2, templateId: 'twelve-month-maximize', delay: { days: 2 }, condition: null },
      { step: 3, templateId: 'twelve-month-first-month-checkin', delay: { days: 30 }, condition: null },
      { step: 4, templateId: 'twelve-month-midpoint', delay: { days: 180 }, condition: null },
      { step: 5, templateId: 'twelve-month-90-days-left', delay: { days: 275 }, condition: null },
      { step: 6, templateId: 'twelve-month-30-days-left', delay: { days: 335 }, condition: null },
      { step: 7, templateId: 'twelve-month-renewal-reminder', delay: { days: 355 }, condition: null },
    ]
  },
  stackcommerceLifetime: {
    name: 'StackCommerce Lifetime Onboarding',
    description: 'Onboard lifetime members with ongoing engagement and skills assessment reminders',
    steps: [
      { step: 1, templateId: 'lifetime-welcome', delay: { minutes: 0 }, condition: null },
      { step: 2, templateId: 'lifetime-maximize', delay: { days: 2 }, condition: null },
      { step: 3, templateId: 'lifetime-skills-assessment-reminder', delay: { days: 7 }, condition: '!skillsAssessmentCompleted' },
      { step: 4, templateId: 'lifetime-first-month-checkin', delay: { days: 30 }, condition: null },
      { step: 5, templateId: 'lifetime-skills-assessment-quarterly', delay: { days: 90 }, condition: null },
      { step: 6, templateId: 'lifetime-6-month-checkin', delay: { days: 180 }, condition: null },
      { step: 7, templateId: 'lifetime-skills-assessment-biannual', delay: { days: 180 }, condition: null },
      { step: 8, templateId: 'lifetime-annual-checkin', delay: { days: 365 }, condition: null },
    ]
  },
};

interface UserEmailFlow {
  currentFlow: string;
  currentStep: number;
  nextEmailAt: Date | null;
  isActive: boolean;
  startedAt: Date | null;
  completedAt: Date | null;
  stoppedAt: Date | null;
  stoppedReason: string | null;
  lastEmailSent: Date | null;
}

interface UserData {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  subscriptionTier: string;
  emailFlow?: UserEmailFlow;
  createdAt: Date;
  skillsAssessmentCompleted?: boolean;
}

interface EmailHistoryItem {
  id: string;
  templateId: string;
  sentAt: Date;
  status: string;
  flowType?: string;
  flowStep?: number;
}

interface EmailTemplate {
  subject: string;
  htmlBody: string;
  textBody: string;
  flow?: string;
  step?: number;
}

const parseFlowData = (flowData: any): UserEmailFlow | undefined => {
  if (!flowData) return undefined;
  return {
    currentFlow: flowData.currentFlow,
    currentStep: flowData.currentStep,
    nextEmailAt: flowData.nextEmailAt?.toDate?.() || null,
    isActive: flowData.isActive,
    startedAt: flowData.startedAt?.toDate?.() || null,
    completedAt: flowData.completedAt?.toDate?.() || null,
    stoppedAt: flowData.stoppedAt?.toDate?.() || null,
    stoppedReason: flowData.stoppedReason || null,
    lastEmailSent: flowData.lastEmailSent?.toDate?.() || null,
  };
};

const getFlowDocField = (_flowKey: string): string => 'emailFlow';

export const EmailFlowVisualizationPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserData[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [flowFilter, setFlowFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const searchInputRef = useRef(null); // Remove type argument for compatibility
  const [emailHistory, setEmailHistory] = useState<EmailHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Inline editing state
  const [isEditingFlow, setIsEditingFlow] = useState(false);
  const [editFlow, setEditFlow] = useState('');
  const [editStep, setEditStep] = useState(1);
  const [editIsActive, setEditIsActive] = useState(true);
  const [savingFlow, setSavingFlow] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Template preview state
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [templateContent, setTemplateContent] = useState<EmailTemplate | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  // Fetch template content from Firestore
  const fetchTemplateContent = useCallback(async (templateId: string) => {
    setSelectedTemplate(templateId);
    setTemplateLoading(true);
    
    try {
      const templateDoc = await getDoc(doc(db, 'email_templates', templateId));
      if (templateDoc.exists()) {
        const data = templateDoc.data();
        setTemplateContent({
          subject: data.subject || 'No subject',
          htmlBody: data.htmlBody || '',
          textBody: data.textBody || '',
          flow: data.flow,
          step: data.step,
        });
      } else {
        setTemplateContent({
          subject: `Template: ${templateId}`,
          htmlBody: '<p>Template not found in database. It may need to be uploaded.</p>',
          textBody: 'Template not found in database. It may need to be uploaded.',
        });
      }
    } catch (err) {
      console.error('Error fetching template:', err);
      setTemplateContent({
        subject: `Template: ${templateId}`,
        htmlBody: '<p>Error loading template content.</p>',
        textBody: 'Error loading template content.',
      });
    } finally {
      setTemplateLoading(false);
    }
  }, []);

  // Close template preview
  const closeTemplatePreview = useCallback(() => {
    setSelectedTemplate(null);
    setTemplateContent(null);
  }, []);

  // Search users by email or name
  const searchUsers = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    setError(null);
    try {
      const searchLower = term.toLowerCase();
      const emailQuery = query(
        collection(db, 'users'),
        where('email', '>=', searchLower),
        where('email', '<=', searchLower + '\uf8ff'),
        limit(10)
      );
      const emailSnapshot = await getDocs(emailQuery);
      const users: UserData[] = [];
      emailSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        users.push({
          id: docSnap.id,
          email: data.email || '',
          firstName: data.firstName,
          lastName: data.lastName,
          displayName: data.displayName,
          subscriptionTier: data.subscriptionTier || 'free',
          emailFlow: parseFlowData(data.emailFlow),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          skillsAssessmentCompleted: data.skillsAssessmentCompleted,
        });
      });
      setSearchResults(users);
      setShowDropdown(true);
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to search users');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounce search as user types
  useEffect(() => {
    const handler = setTimeout(() => {
      // If there is a search term, always search
      if (searchTerm) {
        searchUsers(searchTerm);
        return;
      }
      // If no search term, but filters are set, fetch users by filters
      if (flowFilter || statusFilter) {
        // Fetch users matching filters (limit 10)
        (async () => {
          setSearchLoading(true);
          setError(null);
          try {
            let userQuery = collection(db, 'users');
            let q = query(userQuery, limit(10));
            // No text search, so just get users (filter in-memory)
            const snapshot = await getDocs(q);
            const users: UserData[] = [];
            snapshot.docs.forEach(docSnap => {
              const data = docSnap.data();
              users.push({
                id: docSnap.id,
                email: data.email || '',
                firstName: data.firstName,
                lastName: data.lastName,
                displayName: data.displayName,
                subscriptionTier: data.subscriptionTier || 'free',
                emailFlow: parseFlowData(data.emailFlow),
                createdAt: data.createdAt?.toDate?.() || new Date(),
                skillsAssessmentCompleted: data.skillsAssessmentCompleted,
              });
            });
            setSearchResults(users);
            setShowDropdown(true);
          } catch (err) {
            console.error('Error searching users:', err);
            setError('Failed to search users');
          } finally {
            setSearchLoading(false);
          }
        })();
        return;
      }
      // No search term and no filters
      setSearchResults([]);
      setShowDropdown(false);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm, flowFilter, statusFilter, searchUsers]);

  // Load email history for selected user
  const loadEmailHistory = useCallback(async (userId: string, userEmail: string) => {
    try {
      const mailQuery = query(
        collection(db, 'mail'),
        where('to', 'array-contains', userEmail),
        orderBy('delivery.startTime', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(mailQuery);
      const history: EmailHistoryItem[] = [];

      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        history.push({
          id: docSnap.id,
          templateId: data.template?.name || 'manual',
          sentAt: data.delivery?.startTime?.toDate?.() || new Date(),
          status: data.delivery?.state || 'unknown',
          flowType: data.flowType,
          flowStep: data.flowStep,
        });
      });

      setEmailHistory(history);
    } catch (err) {
      console.error('Error loading email history:', err);
      // Don't set error - email history is supplementary
    }
  }, []);

  // Select a user and load their data
  const selectUser = useCallback(async (user: UserData) => {
    setLoading(true);
    setError(null);

    try {
      // Reload fresh user data
      const userDoc = await getDoc(doc(db, 'users', user.id));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const userData: UserData = {
          id: userDoc.id,
          email: data.email || '',
          firstName: data.firstName,
          lastName: data.lastName,
          displayName: data.displayName,
          subscriptionTier: data.subscriptionTier || 'free',
          emailFlow: parseFlowData(data.emailFlow),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          skillsAssessmentCompleted: data.skillsAssessmentCompleted,
        };
        
        setSelectedUser(userData);
        await loadEmailHistory(user.id, userData.email);
      }
    } catch (err) {
      console.error('Error loading user:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  }, [loadEmailHistory]);

  // Format delay for display
  const formatDelay = (delay: { minutes?: number; hours?: number; days?: number }): string => {
    const parts: string[] = [];
    if (delay.days) parts.push(`${delay.days} day${delay.days > 1 ? 's' : ''}`);
    if (delay.hours) parts.push(`${delay.hours} hour${delay.hours > 1 ? 's' : ''}`);
    if (delay.minutes !== undefined) {
      if (delay.minutes === 0) parts.push('Immediate');
      else parts.push(`${delay.minutes} minute${delay.minutes > 1 ? 's' : ''}`);
    }
    return parts.join(', ') || 'Immediate';
  };

  // Start editing flow
  const startEditingFlow = useCallback(() => {
    const activeFlow = selectedUser?.emailFlow;
    if (activeFlow) {
      setEditFlow(activeFlow.currentFlow);
      setEditStep(activeFlow.currentStep);
      setEditIsActive(activeFlow.isActive);
    } else {
      setEditFlow(Object.keys(EMAIL_FLOWS)[0]);
      setEditStep(1);
      setEditIsActive(true);
    }
    setIsEditingFlow(true);
    setSaveSuccess(false);
  }, [selectedUser]);

  // Cancel editing
  const cancelEditingFlow = useCallback(() => {
    setIsEditingFlow(false);
    setSaveSuccess(false);
  }, []);

  // Calculate next email date based on step delay
  const calculateNextEmailDate = (flowKey: string, step: number): Date => {
    const flow = EMAIL_FLOWS[flowKey];
    if (!flow || !flow.steps[step - 1]) {
      return new Date();
    }
    
    const delay = flow.steps[step - 1].delay;
    const now = new Date();
    
    if (delay.days) now.setDate(now.getDate() + delay.days);
    if (delay.hours) now.setHours(now.getHours() + delay.hours);
    if (delay.minutes) now.setMinutes(now.getMinutes() + delay.minutes);
    
    return now;
  };

  // Save flow changes
  const saveFlowChanges = useCallback(async () => {
    if (!selectedUser) return;
    
    setSavingFlow(true);
    setError(null);
    
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const nextEmailAt = calculateNextEmailDate(editFlow, editStep);
      
      const field = getFlowDocField(editFlow);
      const existingFlow = (selectedUser as any)[field] as UserEmailFlow | undefined;
      await updateDoc(userRef, {
        [`${field}.currentFlow`]: editFlow,
        [`${field}.currentStep`]: editStep,
        [`${field}.isActive`]: editIsActive,
        [`${field}.nextEmailAt`]: Timestamp.fromDate(nextEmailAt),
        [`${field}.stoppedAt`]: editIsActive ? null : Timestamp.now(),
        [`${field}.stoppedReason`]: editIsActive ? null : 'Manually paused by admin',
        [`${field}.startedAt`]: existingFlow?.startedAt ?
          Timestamp.fromDate(existingFlow.startedAt) :
          Timestamp.now(),
        [`${field}.completedAt`]: null,
      });
      
      // Refresh user data
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        setSelectedUser({
          ...selectedUser,
          emailFlow: parseFlowData(data.emailFlow),
        });
      }
      
      setIsEditingFlow(false);
      setSaveSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving flow changes:', err);
      setError('Failed to save flow changes');
    } finally {
      setSavingFlow(false);
    }
  }, [selectedUser, editFlow, editStep, editIsActive]);

  // Get flow status badge
  const getFlowStatusBadge = (emailFlow?: UserEmailFlow) => {
    if (!emailFlow) {
      return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">No Flow</span>;
    }
    if (emailFlow.completedAt) {
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Completed</span>;
    }
    if (emailFlow.stoppedAt) {
      return <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">Stopped</span>;
    }
    if (emailFlow.isActive) {
      return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">Active</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Paused</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Email Flow Visualization</h1>
              <p className="text-gray-600">View and understand the current email flow for any user</p>
            </div>
            {/* Removed Back to Email Management button as requested */}
          </div>
        </div>

        {/* Search & Filter Section */}
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* User Search Dropdown */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  setShowDropdown(true);
                }}
                placeholder="Type to search users..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoComplete="off"
                onFocus={() => searchTerm && setShowDropdown(true)}
                onBlur={() => {
                  // Delay hiding dropdown to allow click
                  setTimeout(() => setShowDropdown(false), 150);
                }}
              />
              {/* Dropdown results */}
              {showDropdown && searchResults.length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                  {searchResults
                    .filter(user => {
                      const anyFlow = user.emailFlow;
                      if (flowFilter) {
                        const flows = [user.emailFlow];
                        if (!flows.some(f => f?.currentFlow === flowFilter)) return false;
                      }
                      if (statusFilter === 'inprogress' && (!anyFlow || !anyFlow.isActive)) return false;
                      if (statusFilter === 'completed' && (!anyFlow || anyFlow.isActive)) return false;
                      return true;
                    })
                    .map(user => (
                      <button
                        key={user.id}
                        onClick={() => {
                          selectUser(user);
                          setShowDropdown(false);
                          setSearchResults([]);
                          setSearchTerm(user.email); // Optionally fill input with selected
                          // Extra: clear searchTerm to force dropdown to close
                          setTimeout(() => {
                            setShowDropdown(false);
                          }, 0);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-blue-50 ${selectedUser?.id === user.id ? 'bg-blue-100' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{user.email}</span>
                          <span className="text-xs text-gray-500">{user.firstName} {user.lastName}</span>
                          <span className="text-xs text-purple-600">{user.subscriptionTier}</span>
                          {getFlowStatusBadge(user.emailFlow)}
                        </div>
                      </button>
                    ))}
                  {searchResults.filter(user => {
                    if (flowFilter && user.emailFlow?.currentFlow !== flowFilter) return false;
                    if (statusFilter === 'inprogress' && (!user.emailFlow || !user.emailFlow.isActive)) return false;
                    if (statusFilter === 'completed' && (!user.emailFlow || user.emailFlow.isActive)) return false;
                    return true;
                  }).length === 0 && (
                    <div className="px-4 py-2 text-gray-500">No users match filters</div>
                  )}
                </div>
              )}
            </div>
            {/* Flow Filter */}
            <div>
              <select
                value={flowFilter}
                onChange={e => setFlowFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Flows</option>
                {Object.entries(EMAIL_FLOWS).map(([key, flow]) => (
                  <option key={key} value={key}>{flow.name}</option>
                ))}
              </select>
            </div>
            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="inprogress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <Button onClick={() => {
              if (searchTerm) {
                searchUsers(searchTerm);
              } else if (flowFilter || statusFilter) {
                // Fetch users by filters only
                (async () => {
                  setSearchLoading(true);
                  setError(null);
                  try {
                    let userQuery = collection(db, 'users');
                    let q = query(userQuery, limit(50));
                    const snapshot = await getDocs(q);
                    let users: UserData[] = [];
                    snapshot.docs.forEach(docSnap => {
                      const data = docSnap.data();
                      users.push({
                        id: docSnap.id,
                        email: data.email || '',
                        firstName: data.firstName,
                        lastName: data.lastName,
                        displayName: data.displayName,
                        subscriptionTier: data.subscriptionTier || 'free',
                        emailFlow: parseFlowData(data.emailFlow),
                        createdAt: data.createdAt?.toDate?.() || new Date(),
                        skillsAssessmentCompleted: data.skillsAssessmentCompleted,
                      });
                    });
                    // Apply filter logic in-memory
                    users = users.filter(user => {
                      const anyFlow = user.emailFlow;
                      if (flowFilter) {
                        const flows = [user.emailFlow];
                        if (!flows.some(f => f?.currentFlow === flowFilter)) return false;
                      }
                      if (statusFilter === 'inprogress' && (!anyFlow || !anyFlow.isActive)) return false;
                      if (statusFilter === 'completed' && (!anyFlow || anyFlow.isActive)) return false;
                      return true;
                    }).slice(0, 10);
                    setSearchResults(users);
                    setShowDropdown(users.length > 0);
                  } catch (err) {
                    console.error('Error searching users:', err);
                    setError('Failed to search users');
                  } finally {
                    setSearchLoading(false);
                  }
                })();
              } else {
                setSearchResults([]);
                setShowDropdown(false);
              }
            }} disabled={searchLoading}>
              {searchLoading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Search
            </Button>
          </div>
        </Card>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="ml-3 text-gray-600">Loading user data...</span>
          </div>
        )}

        {/* User Flow Visualization */}
        {selectedUser && !loading && (
          <div className="space-y-6">
            {/* User Info Card */}
            <Card>
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </h2>
                  <p className="text-gray-600">{selectedUser.email}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                      {selectedUser.subscriptionTier}
                    </span>
                    {getFlowStatusBadge(selectedUser.emailFlow)}
                    {selectedUser.skillsAssessmentCompleted && (
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        ✓ Skills Assessment Complete
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="primary" size="sm" onClick={startEditingFlow}>
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit Flow
                  </Button>
                </div>
              </div>

              {/* Success Message */}
              {saveSuccess && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-800">Flow changes saved successfully!</span>
                  </div>
                </div>
              )}

              {/* Inline Flow Editor */}
              {isEditingFlow && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Email Flow</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {/* Flow Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Flow
                      </label>
                      <select
                        value={editFlow}
                        onChange={(e) => {
                          setEditFlow(e.target.value);
                          setEditStep(1); // Reset step when flow changes
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {Object.entries(EMAIL_FLOWS).map(([key, flow]) => (
                          <option key={key} value={key}>
                            {flow.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Step Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Step
                      </label>
                      <select
                        value={editStep}
                        onChange={(e) => setEditStep(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {EMAIL_FLOWS[editFlow]?.steps.map((step) => (
                          <option key={step.step} value={step.step}>
                            Step {step.step}: {step.templateId}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Status Toggle */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Flow Status
                      </label>
                      <div className="flex items-center gap-4 mt-2">
                        <button
                          onClick={() => setEditIsActive(true)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                            editIsActive 
                              ? 'border-green-500 bg-green-50 text-green-700' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Play className="w-4 h-4" />
                          Active
                        </button>
                        <button
                          onClick={() => setEditIsActive(false)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                            !editIsActive 
                              ? 'border-orange-500 bg-orange-50 text-orange-700' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Pause className="w-4 h-4" />
                          Paused
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Preview of selected step */}
                  {EMAIL_FLOWS[editFlow]?.steps[editStep - 1] && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                      <h4 className="font-medium text-blue-900 mb-2">Selected Step Preview</h4>
                      <p className="text-sm text-blue-800">
                        <strong>Template:</strong> {EMAIL_FLOWS[editFlow].steps[editStep - 1].templateId}
                      </p>
                      <p className="text-sm text-blue-800">
                        <strong>Delay:</strong> {formatDelay(EMAIL_FLOWS[editFlow].steps[editStep - 1].delay)}
                      </p>
                      {EMAIL_FLOWS[editFlow].steps[editStep - 1].condition && (
                        <p className="text-sm text-blue-800">
                          <strong>Condition:</strong> {EMAIL_FLOWS[editFlow].steps[editStep - 1].condition}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button 
                      variant="primary" 
                      onClick={saveFlowChanges}
                      disabled={savingFlow}
                    >
                      {savingFlow ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="secondary" 
                      onClick={cancelEditingFlow}
                      disabled={savingFlow}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Current Flow Details — show a card for each active flow */}
            {(() => {
              const activeFlows: Array<{ key: string; label: string; flow: UserEmailFlow }> = [];
              if (selectedUser.emailFlow) activeFlows.push({ key: 'emailFlow', label: 'TrainedUp', flow: selectedUser.emailFlow });

              if (activeFlows.length === 0) {
                return (
                  <Card>
                    <div className="text-center py-8">
                      <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Email Flow</h3>
                      <p className="text-gray-600 mb-4">
                        This user is not currently in any email flow.
                      </p>
                      <Button variant="primary" onClick={startEditingFlow}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        Assign Email Flow
                      </Button>
                    </div>

                    {isEditingFlow && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Assign Email Flow</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Email Flow</label>
                            <select value={editFlow} onChange={(e) => { setEditFlow(e.target.value); setEditStep(1); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                              {Object.entries(EMAIL_FLOWS).map(([key, flow]) => (<option key={key} value={key}>{flow.name}</option>))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Starting Step</label>
                            <select value={editStep} onChange={(e) => setEditStep(parseInt(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                              {EMAIL_FLOWS[editFlow]?.steps.map((step) => (<option key={step.step} value={step.step}>Step {step.step}: {step.templateId}</option>))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Start As</label>
                            <div className="flex items-center gap-4 mt-2">
                              <button onClick={() => setEditIsActive(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${editIsActive ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-gray-300'}`}><Play className="w-4 h-4" />Active</button>
                              <button onClick={() => setEditIsActive(false)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${!editIsActive ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300'}`}><Pause className="w-4 h-4" />Paused</button>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <Button variant="primary" onClick={saveFlowChanges} disabled={savingFlow}>
                            {savingFlow ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Assign Flow</>}
                          </Button>
                          <Button variant="secondary" onClick={cancelEditingFlow} disabled={savingFlow}><X className="w-4 h-4 mr-2" />Cancel</Button>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              }

              return (
                <>
                  {activeFlows.map(({ key, label, flow }) => (
                    <Card key={key}>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {EMAIL_FLOWS[flow.currentFlow]?.name || flow.currentFlow}
                      </h3>
                      <p className="text-xs text-gray-400 mb-2">{label} flow</p>
                      <p className="text-gray-600 mb-6">
                        {EMAIL_FLOWS[flow.currentFlow]?.description || 'Custom email flow'}
                      </p>

                      {/* Flow Timeline */}
                      <div className="relative">
                        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                        {EMAIL_FLOWS[flow.currentFlow]?.steps.map((step, index) => {
                          const isCompleted = step.step < flow.currentStep;
                          const isCurrent = step.step === flow.currentStep;
                          return (
                            <div key={step.step} className="relative flex items-start mb-6 last:mb-0">
                              <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 ${isCompleted ? 'bg-green-100 border-green-500' : isCurrent ? 'bg-blue-100 border-blue-500' : 'bg-gray-100 border-gray-300'}`}>
                                {isCompleted ? <CheckCircle className="w-6 h-6 text-green-600" /> : isCurrent ? (flow.isActive ? <Clock className="w-6 h-6 text-blue-600" /> : <Pause className="w-6 h-6 text-blue-600" />) : <span className="text-gray-500 font-medium">{step.step}</span>}
                              </div>
                              <button onClick={() => fetchTemplateContent(step.templateId)} className={`ml-4 flex-1 p-4 rounded-lg text-left transition-all hover:shadow-md hover:scale-[1.01] cursor-pointer ${isCurrent ? 'bg-blue-50 border border-blue-200 hover:border-blue-300' : 'bg-gray-50 hover:bg-gray-100'}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Eye className="w-4 h-4 text-gray-400" />
                                    <h4 className="font-medium text-gray-900">{step.templateId}</h4>
                                  </div>
                                  <span className={`text-sm ${isCompleted ? 'text-green-600' : isCurrent ? 'text-blue-600' : 'text-gray-500'}`}>{formatDelay(step.delay)}</span>
                                </div>
                                {step.condition && <p className="text-sm text-gray-600 mb-2"><span className="font-medium">Condition:</span> {step.condition}</p>}
                                {isCurrent && flow.nextEmailAt && <div className="flex items-center gap-2 text-sm text-blue-700"><Calendar className="w-4 h-4" /><span>Scheduled: {flow.nextEmailAt.toLocaleString()}</span></div>}
                                {isCompleted && index === flow.currentStep - 2 && flow.lastEmailSent && <div className="flex items-center gap-2 text-sm text-green-700"><CheckCircle className="w-4 h-4" /><span>Sent: {flow.lastEmailSent.toLocaleString()}</span></div>}
                                <p className="text-xs text-gray-400 mt-2">Click to preview template</p>
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      {/* Flow Metadata */}
                      <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div><p className="text-sm text-gray-500">Started</p><p className="font-medium">{flow.startedAt?.toLocaleDateString() || 'N/A'}</p></div>
                        <div><p className="text-sm text-gray-500">Current Step</p><p className="font-medium">{flow.currentStep}</p></div>
                        <div><p className="text-sm text-gray-500">Next Email</p><p className="font-medium">{flow.nextEmailAt?.toLocaleDateString() || 'N/A'}</p></div>
                        <div><p className="text-sm text-gray-500">Status</p><p className="font-medium">{flow.isActive ? 'Active' : flow.completedAt ? 'Completed' : flow.stoppedReason || 'Paused'}</p></div>
                      </div>
                      {flow.stoppedReason && (
                        <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-orange-600" />
                            <span className="font-medium text-orange-800">Flow stopped:</span>
                            <span className="text-orange-700">{flow.stoppedReason}</span>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </>
              );
            })()}

            {/* Email History */}
            {emailHistory.length > 0 && (
              <Card>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Email History</h3>
                <div className="space-y-3">
                  {emailHistory.map(item => (
                    <div 
                      key={item.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {item.status === 'SUCCESS' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : item.status === 'ERROR' ? (
                          <XCircle className="w-5 h-5 text-red-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-yellow-600" />
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{item.templateId}</p>
                          <p className="text-sm text-gray-500">
                            {item.sentAt.toLocaleString()}
                            {item.flowType && ` • ${item.flowType} Step ${item.flowStep}`}
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        item.status === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                        item.status === 'ERROR' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {item.status}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* All Available Flows Reference */}
            <Card>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Available Email Flows</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(EMAIL_FLOWS).map(([key, flow]) => (
                  <div 
                    key={key}
                    className={`p-4 rounded-lg border ${
                      selectedUser.emailFlow?.currentFlow === key
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <h4 className="font-medium text-gray-900">{flow.name}</h4>
                    <p className="text-sm text-gray-600 mb-2">{flow.description}</p>
                    <p className="text-xs text-gray-500">{flow.steps.length} steps</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Empty State */}
        {!selectedUser && !loading && searchResults.length === 0 && (
          <Card>
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Search for a User</h3>
              <p className="text-gray-600">
                Enter an email address above to view their email flow status.
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* Template Preview Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-purple-600" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selectedTemplate}</h3>
                  {templateContent && (
                    <p className="text-sm text-gray-600">{templateContent.subject}</p>
                  )}
                </div>
              </div>
              <button
                onClick={closeTemplatePreview}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-4">
              {templateLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                  <span className="ml-3 text-gray-600">Loading template...</span>
                </div>
              ) : templateContent ? (
                <div className="space-y-6">
                  {/* Subject Line */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Subject Line</h4>
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <p className="text-gray-900">{templateContent.subject}</p>
                    </div>
                  </div>
                  
                  {/* HTML Preview */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">HTML Preview</h4>
                    <div className="border border-gray-200 rounded-lg p-4 bg-white">
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(templateContent.htmlBody) }}
                      />
                    </div>
                  </div>
                  
                  {/* Plain Text Preview */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Plain Text Version</h4>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                        {templateContent.textBody}
                      </pre>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No template content available</p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <Button variant="secondary" onClick={closeTemplatePreview}>
                Close Preview
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailFlowVisualizationPage;
